import axios, { type InternalAxiosRequestConfig } from 'axios';
import { useAdminStore } from '@/store/admin-store';

// ─── Axios instance ───────────────────────────────────────────────────────────

const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';

export const http = axios.create({
  baseURL: API_BASE,
  timeout: 15_000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// ─── Auth interceptor ─────────────────────────────────────────────────────────

http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem('admin_token');
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Single-flight refresh: when the short-lived access token expires, the first
// 401 transparently refreshes it and retries the request, instead of logging
// the admin out. This is what previously caused "searching logs me out and
// loses my unsaved Add-Product form" — the access token had simply expired and
// there was no refresh, so the next call (often a search) dumped the session.
let refreshPromise: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
  const refreshToken = localStorage.getItem('admin_refresh_token');
  if (!refreshToken) throw new Error('no refresh token');
  const res = await axios.post(`${API_BASE}/auth/refresh`, { refreshToken });
  const { accessToken, refreshToken: newRefresh } = res.data.data;
  localStorage.setItem('admin_token', accessToken);
  if (newRefresh) localStorage.setItem('admin_refresh_token', newRefresh);
  useAdminStore.getState().setTokens(accessToken, newRefresh ?? refreshToken);
  return accessToken;
}

function forceLogout() {
  const { isAuthenticated, logout } = useAdminStore.getState();
  if (isAuthenticated) logout();
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
}

http.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const status = error.response?.status;

    // Don't try to refresh the refresh/login calls themselves.
    const isAuthCall = typeof original?.url === 'string' && /\/auth\/(refresh|login)/.test(original.url);

    if (status === 401 && original && !original._retry && !isAuthCall) {
      original._retry = true;
      try {
        refreshPromise = refreshPromise ?? refreshAccessToken();
        const token = await refreshPromise;
        refreshPromise = null;
        if (original.headers) original.headers.Authorization = `Bearer ${token}`;
        return http(original);
      } catch {
        refreshPromise = null;
        forceLogout();
        return Promise.reject(error);
      }
    }

    if (status === 401) forceLogout();
    return Promise.reject(error);
  }
);

// ─── Types ────────────────────────────────────────────────────────────────────

type Params = Record<string, string | number | boolean | undefined>;

// ─── Admin API ────────────────────────────────────────────────────────────────

export const adminApi = {
  login: (email: string, password: string) =>
    http.post('/auth/login', { identifier: email, password }),

  logout: () => http.post('/auth/logout'),

  getMe: () => http.get('/auth/me'),

  // Dashboard
  getDashboardStats: () => http.get('/admin/dashboard'),

  // Vendors
  getVendors: (params?: Params) => http.get('/admin/vendors', { params }),

  getVendor: (id: string) => http.get(`/admin/vendors/${id}`),

  // Resolve a stored KYC doc key into a short-lived viewable URL (private
  // bucket, streamed through the authenticated API). Returns '' if unavailable.
  getKycViewUrl: async (vendorId: string, doc: string): Promise<string> => {
    const res = await http.get('/uploads/kyc/url', { params: { vendorId, doc } });
    const data = (res.data?.data ?? res.data) as { token?: string; url?: string };
    if (data?.token) return `${API_BASE}/uploads/kyc/view?token=${encodeURIComponent(data.token)}`;
    return data?.url ?? '';
  },

  approveVendor: (id: string, notes?: string) =>
    http.patch(`/admin/vendors/${id}/approve`, { internalNotes: notes }),

  rejectVendor: (
    id: string,
    reason: string,
    notes?: string,
    documentReviews?: Record<string, { status: 'pending' | 'approved' | 'rejected'; note?: string }>,
  ) =>
    http.patch(`/admin/vendors/${id}/reject`, { reason, internalNotes: notes, documentReviews }),

  suspendVendor: (id: string, reason: string) =>
    http.patch(`/admin/vendors/${id}/suspend`, { reason }),

  reactivateVendor: (id: string) => http.patch(`/admin/vendors/${id}/reactivate`),

  updateVendorTier: (id: string, tier: string) =>
    http.patch(`/admin/vendors/${id}/tier`, { tier }),

  // Users / Customers
  getUsers: (params?: Params) => http.get('/admin/users', { params }),

  getUser: (id: string) => http.get(`/admin/users/${id}`),

  suspendUser: (id: string, reason: string) =>
    http.post(`/admin/users/${id}/suspend`, { reason }),

  reactivateUser: (id: string) => http.post(`/admin/users/${id}/reactivate`),

  // Products / Catalog (uses real /products endpoints with admin role)
  getProducts: (params?: Params) => http.get('/products', { params }),

  getProduct: (id: string) => http.get(`/products/${id}`),

  createProduct: (data: Record<string, unknown> & { vendorId: string; categoryId: string }) =>
    http.post('/products', data),

  // "Approve" moves draft → active via the publish endpoint.
  approveProduct: (id: string) => http.patch(`/products/${id}/publish`),

  // "Reject" archives the product. The API doesn't model rejection reasons
  // on the product itself yet, so the reason is currently dropped.
  rejectProduct: (id: string) =>
    http.put(`/products/${id}`, { status: 'archived' }),

  featureProduct: (id: string, featured: boolean) =>
    http.put(`/products/${id}`, { isFeatured: featured }),

  // Permanently removes the product (variants cascade; order history is kept).
  deleteProduct: (id: string) => http.delete(`/products/${id}`),

  // Orders
  getOrders: (params?: Params) => http.get('/admin/orders', { params }),

  getOrder: (id: string) => http.get(`/admin/orders/${id}`),

  getOrderStats: () => http.get('/admin/orders/stats'),

  updateOrderStatus: (id: string, status: string, note?: string) =>
    http.patch(`/admin/orders/${id}/status`, { status, note }),

  setOrderTracking: (id: string, trackingNumber: string, courierName: string) =>
    http.patch(`/admin/orders/${id}/tracking`, { trackingNumber, courierName }),

  confirmOrderPayment: (id: string) =>
    http.patch(`/admin/orders/${id}/confirm-payment`),

  resolveDispute: (orderId: string, resolution: string) =>
    http.post(`/admin/orders/${orderId}/dispute/resolve`, { resolution }),

  // Staff (uses /admin/staff endpoints)
  getStaffPermissions: () => http.get('/admin/staff/permissions/catalog'),

  getStaffRolePresets: () => http.get('/admin/staff/roles'),

  getStaffList: () => http.get('/admin/staff'),

  createStaff: (data: {
    email?: string;
    phone?: string;
    firstName?: string;
    lastName?: string;
    password: string;
    permissions: string[];
  }) => http.post('/admin/staff', data),

  updateStaffPermissions: (id: string, permissions: string[]) =>
    http.patch(`/admin/staff/${id}/permissions`, { permissions }),

  deactivateStaff: (id: string) => http.delete(`/admin/staff/${id}`),

  reactivateStaff: (id: string) => http.patch(`/admin/staff/${id}/reactivate`),

  hardDeleteStaff: (id: string) => http.delete(`/admin/staff/${id}/permanent`),

  // Categories
  getCategories: (params?: Params) => http.get('/categories', { params }),
  // Admin manages the full tree including hidden categories, so deactivating a
  // parent doesn't make its children look deleted.
  getCategoryTree: () => http.get('/categories/tree', { params: { includeInactive: true } }),
  getCategory: (id: string) => http.get(`/categories/${id}`),
  createCategory: (data: Record<string, unknown>) => http.post('/categories', data),
  updateCategory: (id: string, data: Record<string, unknown>) =>
    http.put(`/categories/${id}`, data),
  deleteCategory: (id: string) => http.delete(`/categories/${id}`),

  // Uploads
  uploadImage: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return http.post<{ data: { url: string; filename: string; size: number } }>(
      '/uploads/image',
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
  },

  uploadImages: (files: File[]) => {
    const fd = new FormData();
    for (const f of files) fd.append('files', f);
    return http.post<{ data: Array<{ url: string; filename: string; size: number }> }>(
      '/uploads/images',
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
  },

  // Payments / Transactions
  getTransactions: (params?: Params) => http.get('/admin/transactions', { params }),

  getPayoutRequests: (params?: Params) => http.get('/admin/payouts', { params }),

  getPayoutsSummary: () => http.get('/admin/payouts/summary'),

  processPayout: (payoutId: string) =>
    http.post(`/admin/payouts/${payoutId}/process`),

  rejectPayout: (payoutId: string, reason: string) =>
    http.post(`/admin/payouts/${payoutId}/reject`, { reason }),

  // Analytics
  getPlatformAnalytics: (params?: Params) =>
    http.get('/admin/analytics', { params }),

  getVendorAnalytics: (vendorId: string, params?: Params) =>
    http.get(`/admin/analytics/vendors/${vendorId}`, { params }),

  // Settings
  getPlatformSettings: () => http.get('/admin/settings'),

  updatePlatformSettings: (data: Record<string, unknown>) =>
    http.patch('/admin/settings', data),

  updateCommissionRates: (rates: Record<string, number>) =>
    http.patch('/admin/settings/commission', { rates }),

  // System — uses the public /health endpoint (no /admin/system module yet).
  getSystemHealth: () => http.get('/health'),
};
